import { QUESTION_PERMISSIONS, TEMPLATE_PERMISSIONS } from '@hubertai/screening-question-types';
import type { TVisibility } from 'types/template/template.types';
import type { TAdminUser, TRecruiterAppUser } from 'types/user.types';
import { isRecruiterUser } from 'types/user.types';

export type TAccessGroupRef = { _id: string; name: string };

/**
 * A question created inside a local template takes the template's access groups that the recruiter is
 * a member of (see templateGroupsForNewQuestion). The backend accepts that as long as they can create
 * questions in at least one of them, so the action is only blocked when they can't create in any —
 * then the message names the template's groups, any of which an admin could give them create rights in.
 *
 * `templateAccessGroups` must contain only groups that still exist. A soft-deleted access group is
 * one nobody can be given rights in, so naming it would be a dead end — and the template's raw
 * access-group *resource* rows (what `useTemplateAccessGroupIds` exposes) do NOT qualify, because
 * they outlive the group they point at. Use the template access-groups endpoint, which filters on
 * the group document's `deleted` fields.
 *
 * Returns the groups to name in the message; empty means the action is allowed. Hubert admins and
 * non-local templates always come back empty — only local templates are access-group scoped at all.
 */
export const getMissingTemplateAccessGroups = (
  user: TAdminUser | TRecruiterAppUser | undefined | null,
  templateVisibility: TVisibility | undefined | null,
  templateAccessGroups: TAccessGroupRef[] | undefined,
): TAccessGroupRef[] => {
  if (!isRecruiterUser(user)) return [];
  if (templateVisibility !== 'local') return [];

  const groupIdsWithCreate = new Set(
    (user.access_groups_permissions ?? [])
      .filter((ag) => ag.permissions.includes(QUESTION_PERMISSIONS.Create))
      .map((ag) => ag.accessGroupId),
  );
  const groups = templateAccessGroups ?? [];
  const canCreateInSomeGroup = groups.some((ag) => groupIdsWithCreate.has(ag._id));

  return canCreateInSomeGroup ? [] : groups;
};

/**
 * The access groups a question created inside a template starts with: the template's groups that the
 * recruiter is a member of, with any role. The backend refuses groups the caller isn't a member of,
 * and narrows a copy made for a template the same way. Hubert admins keep every template group.
 */
export const templateGroupsForNewQuestion = (
  user: TAdminUser | TRecruiterAppUser | undefined | null,
  templateAccessGroupIds: string[] | undefined,
): string[] | undefined => {
  if (!templateAccessGroupIds || !isRecruiterUser(user)) return templateAccessGroupIds;

  const memberGroupIds = new Set((user.access_groups_permissions ?? []).map((ag) => ag.accessGroupId));
  return templateAccessGroupIds.filter((id) => memberGroupIds.has(id));
};

export type TAccessGroupOption = { label: string; value: string };

/** Whether the user may share a template with their whole organization. Hubert admins always can. */
export const canShareTemplateWithOrg = (user: TAdminUser | TRecruiterAppUser | undefined | null): boolean =>
  !isRecruiterUser(user) ||
  !!user.userRoles?.includes('recruiter-admin') ||
  !!user.access_groups_permissions?.some((x) => x.permissions.includes(TEMPLATE_PERMISSIONS.ShareOrg));

/** Whether the user may create templates, including copies. Hubert admins always can. */
export const canCreateTemplates = (user: TAdminUser | TRecruiterAppUser | undefined | null): boolean =>
  !isRecruiterUser(user) ||
  !!user.userRoles?.includes('recruiter-admin') ||
  !!user.access_groups_permissions?.some((x) => x.permissions.includes(TEMPLATE_PERMISSIONS.Create));

/**
 * The groups a recruiter can create a local template in: `template:create` in at least one chosen
 * group plus membership, with any role, in the others — the backend's create rule. So every group
 * they are a member of is offered, unless they can't create templates in any group at all.
 * Hubert admins get none here; they pick from the destination client's groups.
 */
export const getTemplateCreateAccessGroupOptions = (
  user: TAdminUser | TRecruiterAppUser | undefined | null,
): TAccessGroupOption[] => {
  if (!isRecruiterUser(user)) return [];
  if (!getTemplateCreateAccessGroupIds(user)?.length) return [];

  return (user.access_groups_permissions ?? []).map((x) => ({ label: x.accessGroupName, value: x.accessGroupId }));
};

/** The groups a recruiter can create templates in. Undefined for Hubert admins, who aren't limited by group. */
export const getTemplateCreateAccessGroupIds = (
  user: TAdminUser | TRecruiterAppUser | undefined | null,
): string[] | undefined => {
  if (!isRecruiterUser(user)) return undefined;

  return (user.access_groups_permissions ?? [])
    .filter((x) => x.permissions.includes(TEMPLATE_PERMISSIONS.Create))
    .map((x) => x.accessGroupId);
};

export const chosenAccessGroupsAllowTemplateCreate = (
  chosenAccessGroupIds: string[],
  templateCreateAccessGroupIds: string[] | undefined,
): boolean => {
  if (!templateCreateAccessGroupIds) return true;
  return chosenAccessGroupIds.some((id) => templateCreateAccessGroupIds.includes(id));
};
